import Mention from '@tiptap/extension-mention'
import StarterKit from '@tiptap/starter-kit'
import type { Extensions } from '@tiptap/core'

/**
 * THE list of Tiptap extensions. One definition, imported by both the editor in apps/web and the
 * Y.Doc → markdown transformer on the server.
 *
 * This file existing in one place is a hard requirement, not tidiness (ADR 0006, RULES §7). Two
 * lists that drift apart mean elements of a note — a code block, a list, a mention — silently
 * disappear the next time an agent writes to it, and it looks like an agent bug rather than a
 * config mismatch.
 *
 * History is OFF on purpose: undo belongs to Yjs's UndoManager. Leaving Tiptap's history on makes
 * ctrl+Z start undoing the *other person's* edits.
 */
export function noteExtensions(options: NoteExtensionOptions = {}): Extensions {
  return [
    StarterKit.configure({
      undoRedo: false,
    }),
    Mention.configure({
      HTMLAttributes: { class: 'mention' },
      // Rendered as plain "@name" so the markdown render (and therefore search, and everything an
      // agent reads) contains the text a person actually typed.
      renderText: ({ node }) => `@${String(node.attrs.label ?? node.attrs.id ?? '')}`,
      ...(options.suggestion ? { suggestion: options.suggestion } : {}),
    }),
  ]
}

/**
 * The only thing the browser adds on top: the popup that appears while typing `@`.
 *
 * It is passed in rather than defined here because it needs a DOM and a list of people, neither of
 * which exists on the server. Crucially it does **not** change the schema — the node is identical
 * on both sides, which is what ADR 0006 is actually protecting.
 */
export interface NoteExtensionOptions {
  suggestion?: Record<string, unknown> | undefined
}
